import React, { useCallback, useRef } from "react";
import styled from "styled-components";
import { Box } from "@bca/ui-library";

import ImageCarousel from "./ImageCarousel";
import Thumbnails from "./Thumbnails";
import { Image } from "../vehicleDetails/vehicle-details-dto.type";
import { addDuplicates } from "../helpers/addDuplicates";
import pushToDigitalData from "../analytics/pushToDigitalData";
import getFilteredImagesByType from "../helpers/getFilteredImagesByType";
import { shallowEqual, useSelector } from "react-redux";
import { StoreType } from "../redux/StoreType";
import { GET_USER_STATE } from "../redux/slices/userSlice";
import { GET_VENDOR_IMPERSONATION_STATE } from "../redux/slices/impersonationSlice";

let withTransition = true;
let blockClick = false;
interface ImageGalleryProps {
  images: Image[];
  overlaySlot?: React.ReactNode;
  slideSpacing: number;
  activeImageIndex: number;
  setActiveImageIndex: (newValue: number) => void;
  activeImageIndexRef: any;
  hasImageFullWidth?: boolean;
  disableSwipe?: boolean;
  children: React.ReactNode;
}

export const GalleryContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  background-image: linear-gradient(#080f22, #070b16);
`;

const pushCarouselArrowsToAnalytics = (
  images: Image[],
  activeImageIndex: number,
  hasImageFullWidth: boolean,
  isVendorAdmin: boolean,
  isVendorImpersonationSet: boolean
) => {
  const wantedImage = images[activeImageIndex - 1];
  const wantedImageType = wantedImage?.imageType.toLowerCase();

  const filteredImages = getFilteredImagesByType(
    images,
    wantedImageType === "damage" ? "damage" : "beauty"
  );

  const indexOfWantedImagePerCategory = filteredImages.indexOf(wantedImage) + 1;

  if ((isVendorAdmin && isVendorImpersonationSet) || !isVendorAdmin) {
    pushToDigitalData({
      event: {
        name: "click",
        category: "button",
        label: `Image|${activeImageIndex}`,
        action: "scroll",
        section: hasImageFullWidth ? "gallery" : "in-page",
      },
      product: {
        image: {
          imageType:
            wantedImage?.imageType.toLowerCase() === "damage"
              ? "damage"
              : "beauty",
          imageNum: indexOfWantedImagePerCategory,
          imagePlacementNum: activeImageIndex,
        },
      },
    });
  }
};

const ImageGallery: React.FunctionComponent<ImageGalleryProps> = ({
  images,
  overlaySlot,
  children,
  slideSpacing,
  activeImageIndex,
  activeImageIndexRef,
  hasImageFullWidth,
  setActiveImageIndex,
  disableSwipe,
}) => {
  const startX = useRef(0);
  const direction = useRef(0);

  const {
    user: { isVendorAdmin },
  }: StoreType = useSelector(GET_USER_STATE, shallowEqual);
  const {
    impersonation: { isVendorImpersonationSet },
  }: StoreType = useSelector(GET_VENDOR_IMPERSONATION_STATE, shallowEqual);

  const imagesWithDuplicates =
    images.length < 2 ? images : addDuplicates(images);
  const firstImageIndex = 2;
  const lastImageIndex = imagesWithDuplicates.length - 3;
  const hasOneImage = images.length === 1;

  const handleTouchStart = useCallback((evt) => {
    startX.current = evt.touches[0].pageX;
    direction.current = 0;
  }, []);

  const handleTouchMove = useCallback(
    (evt) => {
      if (disableSwipe) return;
      if (startX.current < evt.touches[0].pageX) {
        direction.current = -1;
        return;
      }
      if (startX.current > evt.touches[0].pageX) {
        direction.current = 1;
        return;
      }

      direction.current = 0;
    },
    [disableSwipe]
  );

  const handleTouchEnd = useCallback(() => {
    if (direction.current === 0 || hasOneImage) {
      return;
    }

    if (direction.current < 0) {
      goToPreviousImage();
      return;
    }
    goToNextImage();
  }, []);

  const goToPreviousImage = useCallback(() => {
    if (blockClick) {
      return;
    }

    blockClick = true;
    setTimeout(() => {
      blockClick = false;
    }, 500);

    if (activeImageIndexRef.current === firstImageIndex) {
      setActiveImageIndex(activeImageIndexRef.current - 1);
      setTimeout(() => {
        withTransition = false;
        setActiveImageIndex(imagesWithDuplicates.length - 3);
        pushCarouselArrowsToAnalytics(
          images,
          images.length,
          hasImageFullWidth,
          isVendorAdmin,
          isVendorImpersonationSet
        );
        withTransition = true;
      }, 500);
      return;
    }

    pushCarouselArrowsToAnalytics(
      images,
      activeImageIndexRef.current - 2,
      hasImageFullWidth,
      isVendorAdmin,
      isVendorImpersonationSet
    );
    setActiveImageIndex(activeImageIndexRef.current - 1);
  }, [
    activeImageIndexRef,
    setActiveImageIndex,
    imagesWithDuplicates.length,
    images,
    hasImageFullWidth,
  ]);

  const goToNextImage = useCallback(() => {
    if (blockClick) {
      return;
    }

    blockClick = true;
    setTimeout(() => {
      blockClick = false;
    }, 500);

    if (activeImageIndexRef.current === lastImageIndex) {
      setActiveImageIndex(activeImageIndexRef.current + 1);
      setTimeout(() => {
        withTransition = false;
        setActiveImageIndex(2);

        pushCarouselArrowsToAnalytics(
          images,
          1,
          hasImageFullWidth,
          isVendorAdmin,
          isVendorImpersonationSet
        );
        withTransition = true;
      }, 500);
      return;
    }

    pushCarouselArrowsToAnalytics(
      images,
      activeImageIndexRef.current,
      hasImageFullWidth,
      isVendorAdmin,
      isVendorImpersonationSet
    );
    setActiveImageIndex(activeImageIndexRef.current + 1);
  }, [
    activeImageIndexRef,
    setActiveImageIndex,
    lastImageIndex,
    images,
    hasImageFullWidth,
  ]);

  return (
    <GalleryContainer pb={hasImageFullWidth ? 0 : 20} pt={10} height="100%">
      <ImageCarousel
        slideSpacing={slideSpacing}
        activeImageIndex={activeImageIndex}
        goToPreviousImage={goToPreviousImage}
        goToNextImage={goToNextImage}
        withTransition={withTransition}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        hasImageFullWidth={hasImageFullWidth}
      >
        {children}
      </ImageCarousel>
      {overlaySlot && overlaySlot}
      <Thumbnails
        images={images}
        activeImageIndex={activeImageIndex}
        setActiveImageIndex={setActiveImageIndex}
        title="VEHICLE IMAGES"
        hasImageFullWidth={hasImageFullWidth}
      />
    </GalleryContainer>
  );
};

export default ImageGallery;
